import { useState, useCallback, useEffect, useMemo } from 'react';
import { useProductService } from 'services/products';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { getCacheKeyWithCompany } from 'utils/cacheUtils';
import { Product } from 'types/product';

const INVENTORY_CACHE_KEY_BASE = 'inventory_cache';
const CACHE_EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes in milliseconds

const sortProductsByDateDesc = (products: Product[]) =>
  [...products].sort((a, b) => {
    const aTime = new Date(a.updatedAt).getTime();
    const bTime = new Date(b.updatedAt).getTime();

    if (Number.isNaN(aTime) || Number.isNaN(bTime)) {
      return 0;
    }

    return bTime - aTime;
  });

interface UseInventoryDataReturn {
  products: Product[];
  loading: boolean;
  reloading: boolean;
  error: string | null;
  reload: () => void;
  updateProducts: (products: Product[]) => void;
}

export function useInventoryData(): UseInventoryDataReturn {
  const productService = useProductService();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [cacheChecked, setCacheChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create cache key with company ID suffix
  const inventoryCacheKey = useMemo(
    () => getCacheKeyWithCompany(INVENTORY_CACHE_KEY_BASE, activeCompanyId),
    [activeCompanyId],
  );

  // Use localStorage hook with encryption and expiration
  const {
    data: cachedProducts,
    setData: setCachedProducts,
    isInitialized: cacheInitialized,
  } = useLocalStorage<Product[]>({
    key: inventoryCacheKey,
    expirationMs: CACHE_EXPIRATION_MS,
    encrypt: true,
  });

  // Reset when company changes (cache key changes)
  useEffect(() => {
    setLoading(true);
    setProducts([]);
    setCacheChecked(false);
    setError(null);
  }, [activeCompanyId]);

  // Load cached data on mount or when company/cache key changes
  useEffect(() => {
    if (!cacheInitialized || !isCompanyInitialized) return; // Wait for both to be ready

    if (cachedProducts) {
      // Use cached data - it's still valid (hook already checked expiration)
      setProducts(sortProductsByDateDesc(cachedProducts));
      setLoading(false);
      setCacheChecked(true);
    } else {
      // No cache or expired - mark as checked so we can fetch
      setCacheChecked(true);
    }
  }, [
    cachedProducts,
    cacheInitialized,
    isCompanyInitialized,
    inventoryCacheKey,
  ]);

  const fetchProducts = useCallback(
    async (isReload = false) => {
      // Don't fetch if company is not initialized yet
      if (!isCompanyInitialized || !activeCompanyId) {
        return;
      }

      // Fetch fresh data (either cache expired, doesn't exist, or isReload is true)
      if (isReload) {
        setReloading(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const productsData = await productService.getAllProducts(false);
        // Filter only products that track inventory and are not voided
        const inventoryProducts = productsData.filter(
          (product) => product.trackInventory && !product.isVoided,
        );
        const sortedProducts = sortProductsByDateDesc(inventoryProducts);
        setProducts(sortedProducts);

        // Limit cached products to first 20 to avoid large data issues
        const productsToCache =
          sortedProducts.length > 20
            ? sortedProducts.slice(0, 20)
            : sortedProducts;

        // Save to cache with encryption
        await setCachedProducts(productsToCache);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch inventory products:', err);
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load inventory. Please check your connection.';
        setError(errorMessage);
      } finally {
        if (isReload) {
          setReloading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [productService, setCachedProducts, isCompanyInitialized, activeCompanyId],
  );

  // Only fetch data if cache is checked, company is initialized, and no cached data exists
  useEffect(() => {
    if (
      cacheChecked &&
      cacheInitialized &&
      isCompanyInitialized &&
      !cachedProducts
    ) {
      void fetchProducts();
    }
  }, [
    cacheChecked,
    cacheInitialized,
    isCompanyInitialized,
    cachedProducts,
    fetchProducts,
  ]);

  const reload = useCallback(() => {
    void fetchProducts(true);
  }, [fetchProducts]);

  const updateProducts = useCallback(
    (updatedProducts: Product[]) => {
      const sortedProducts = sortProductsByDateDesc(updatedProducts);
      setProducts(sortedProducts);

      // Update cache with encrypted data (fire and forget)
      void setCachedProducts(sortedProducts);
    },
    [setCachedProducts],
  );

  return {
    products,
    loading,
    reloading,
    error,
    reload,
    updateProducts,
  };
}
